/**
 * Stored flight session. One open row per tail is the binding.
 * Rebinding closes the old row and opens a new one. It never rewrites the open row in place.
 * The jury may suggest a flight. It cannot open or rebind a session.
 */
import type { CompanyProfile, Mission } from "./types";

export const ORPHAN_BOOT_MAX_AGE_MS = 6 * 60 * 60 * 1000;

export const FLIGHT_SESSION_CLOSE_REASONS = [
  "on_blocks",
  "shutdown",
  "orphan_boot",
  "rebind",
  "crash",
  "manual",
] as const;

export type FlightSessionCloseReason = (typeof FLIGHT_SESSION_CLOSE_REASONS)[number];

export const FLIGHT_SESSION_SOURCES = ["simconnect", "manual", "replay", "desk", "megan"] as const;

export type FlightSessionSource = (typeof FLIGHT_SESSION_SOURCES)[number];

export type FlightSession = {
  id: string;
  missionId: string;
  tail: string;
  openedAt: string;
  closedAt: string | null;
  closeReason: FlightSessionCloseReason | null;
  source: FlightSessionSource;
  deviceId?: string;
  rev: number;
};

export type FlightSessionAuditKind = "open" | "close" | "rebind" | "orphan_boot" | "rejected";

export type FlightSessionAuditEvent = {
  id: string;
  at: string;
  kind: FlightSessionAuditKind;
  sessionId: string;
  missionId: string;
  tail: string;
  fromSessionId?: string;
  toMissionId?: string;
  closeReason?: FlightSessionCloseReason | null;
  detail: string;
};

export type FlightSessionBook = {
  sessions: FlightSession[];
  audit: FlightSessionAuditEvent[];
};

export const EMPTY_FLIGHT_SESSION_BOOK: FlightSessionBook = { sessions: [], audit: [] };

export type SessionOpenResult =
  | { ok: true; book: FlightSessionBook; session: FlightSession; rebound: boolean }
  | { ok: false; book: FlightSessionBook; error: string; code: "tail_busy" | "missing_tail" };

let seq = 0;

export function normalizeSessionTail(raw: string | null | undefined): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}

function nextId(prefix: string, now: Date, tail: string): string {
  seq += 1;
  const stamp = now.getTime().toString(36);
  const clean = normalizeSessionTail(tail).replace(/[^A-Z0-9]/g, "").slice(0, 8) || "tail";
  return `${prefix}-${stamp}-${seq.toString(36)}-${clean}`;
}

function isoOf(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const t = new Date(value);
  if (Number.isNaN(t.getTime())) return null;
  return t.toISOString();
}

function closeReasonOf(value: unknown): FlightSessionCloseReason | null {
  return FLIGHT_SESSION_CLOSE_REASONS.includes(value as FlightSessionCloseReason)
    ? (value as FlightSessionCloseReason)
    : null;
}

function sourceOf(value: unknown): FlightSessionSource {
  return FLIGHT_SESSION_SOURCES.includes(value as FlightSessionSource) ? (value as FlightSessionSource) : "manual";
}

export function isOpenFlightSession(session: Pick<FlightSession, "closedAt">): boolean {
  return !session.closedAt;
}

export function hydrateFlightSession(raw: unknown): FlightSession | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Partial<FlightSession>;
  const id = typeof row.id === "string" ? row.id.trim() : "";
  const missionId = typeof row.missionId === "string" ? row.missionId.trim() : "";
  const tail = normalizeSessionTail(row.tail);
  const openedAt = isoOf(row.openedAt);
  if (!id || !missionId || !tail || !openedAt) return null;
  const closedAt = isoOf(row.closedAt);
  const rev = typeof row.rev === "number" && Number.isFinite(row.rev) ? Math.max(1, Math.round(row.rev)) : 1;
  return {
    id,
    missionId,
    tail,
    openedAt,
    closedAt,
    closeReason: closedAt ? closeReasonOf(row.closeReason) ?? "manual" : null,
    source: sourceOf(row.source),
    deviceId: typeof row.deviceId === "string" && row.deviceId.trim() ? row.deviceId.trim() : undefined,
    rev,
  };
}

export function hydrateFlightSessionAudit(raw: unknown): FlightSessionAuditEvent | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Partial<FlightSessionAuditEvent>;
  const id = typeof row.id === "string" ? row.id.trim() : "";
  const at = isoOf(row.at);
  const sessionId = typeof row.sessionId === "string" ? row.sessionId.trim() : "";
  const missionId = typeof row.missionId === "string" ? row.missionId.trim() : "";
  const tail = normalizeSessionTail(row.tail);
  const kinds: FlightSessionAuditKind[] = ["open", "close", "rebind", "orphan_boot", "rejected"];
  if (!id || !at || !sessionId || !missionId || !tail) return null;
  if (!kinds.includes(row.kind as FlightSessionAuditKind)) return null;
  return {
    id,
    at,
    kind: row.kind as FlightSessionAuditKind,
    sessionId,
    missionId,
    tail,
    fromSessionId: typeof row.fromSessionId === "string" ? row.fromSessionId : undefined,
    toMissionId: typeof row.toMissionId === "string" ? row.toMissionId : undefined,
    closeReason: closeReasonOf(row.closeReason),
    detail: typeof row.detail === "string" ? row.detail : "",
  };
}

export function hydrateFlightSessions(raw: unknown): FlightSession[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(hydrateFlightSession).filter((row): row is FlightSession => Boolean(row));
}

export function hydrateFlightSessionAudits(raw: unknown): FlightSessionAuditEvent[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(hydrateFlightSessionAudit).filter((row): row is FlightSessionAuditEvent => Boolean(row));
}

export function openSessionForTail(sessions: readonly FlightSession[], tail: string): FlightSession | null {
  const key = normalizeSessionTail(tail);
  if (!key) return null;
  const open = sessions.filter((row) => row.tail === key && isOpenFlightSession(row));
  if (!open.length) return null;
  return [...open].sort((a, b) => b.openedAt.localeCompare(a.openedAt) || b.id.localeCompare(a.id))[0] ?? null;
}

function audit(
  now: Date,
  tail: string,
  event: Omit<FlightSessionAuditEvent, "id" | "at" | "tail"> & { at?: string },
): FlightSessionAuditEvent {
  return {
    ...event,
    id: nextId("fsa", now, tail),
    at: event.at ?? now.toISOString(),
    tail: normalizeSessionTail(tail),
  };
}

function closeRow(
  session: FlightSession,
  reason: FlightSessionCloseReason,
  at: string,
): FlightSession {
  return {
    ...session,
    closedAt: at,
    closeReason: reason,
    rev: session.rev + 1,
  };
}

/**
 * Collapse a damaged book to one open session per tail.
 * Older open rows are closed. They are not deleted.
 */
export function enforceOneOpenSessionPerTail(book: FlightSessionBook, now = new Date()): FlightSessionBook {
  const at = now.toISOString();
  const grouped = new Map<string, FlightSession[]>();
  for (const row of book.sessions) {
    if (!isOpenFlightSession(row)) continue;
    const list = grouped.get(row.tail) ?? [];
    list.push(row);
    grouped.set(row.tail, list);
  }
  const closeIds = new Set<string>();
  const extra: FlightSessionAuditEvent[] = [];
  for (const [tail, rows] of grouped) {
    if (rows.length < 2) continue;
    const ranked = [...rows].sort((a, b) => b.openedAt.localeCompare(a.openedAt) || b.id.localeCompare(a.id));
    for (const stale of ranked.slice(1)) {
      closeIds.add(stale.id);
      extra.push(
        audit(now, tail, {
          kind: "orphan_boot",
          sessionId: stale.id,
          missionId: stale.missionId,
          closeReason: "orphan_boot",
          detail: "Closed a second open session for this tail.",
          at,
        }),
      );
    }
  }
  if (!closeIds.size) return book;
  return {
    sessions: book.sessions.map((row) => (closeIds.has(row.id) ? closeRow(row, "orphan_boot", at) : row)),
    audit: [...book.audit, ...extra],
  };
}

export function openFlightSession(
  book: FlightSessionBook,
  input: {
    missionId: string;
    tail: string;
    at?: Date | string;
    source?: FlightSessionSource;
    deviceId?: string;
    rebind?: boolean;
  },
): SessionOpenResult {
  const now = input.at instanceof Date ? input.at : input.at ? new Date(input.at) : new Date();
  const at = Number.isNaN(now.getTime()) ? new Date() : now;
  const iso = at.toISOString();
  const tail = normalizeSessionTail(input.tail);
  const missionId = input.missionId.trim();
  if (!tail || !missionId) {
    return { ok: false, book, error: "A flight session needs a tail and a trip.", code: "missing_tail" };
  }
  const normalized = enforceOneOpenSessionPerTail(book, at);
  const existing = openSessionForTail(normalized.sessions, tail);
  if (existing && existing.missionId === missionId) {
    return { ok: true, book: normalized, session: existing, rebound: false };
  }
  if (existing && !input.rebind) {
    const rejected = audit(at, tail, {
      kind: "rejected",
      sessionId: existing.id,
      missionId: existing.missionId,
      toMissionId: missionId,
      detail: "This tail already has an open session. Rebind closes it first.",
    });
    return {
      ok: false,
      book: { sessions: normalized.sessions, audit: [...normalized.audit, rejected] },
      error: "This tail already has an open flight session.",
      code: "tail_busy",
    };
  }
  const opened: FlightSession = {
    id: nextId("fs", at, tail),
    missionId,
    tail,
    openedAt: iso,
    closedAt: null,
    closeReason: null,
    source: input.source ?? "manual",
    deviceId: input.deviceId,
    rev: 1,
  };
  let sessions = normalized.sessions;
  const events: FlightSessionAuditEvent[] = [];
  if (existing) {
    sessions = sessions.map((row) => (row.id === existing.id ? closeRow(row, "rebind", iso) : row));
    events.push(
      audit(at, tail, {
        kind: "rebind",
        sessionId: existing.id,
        missionId: existing.missionId,
        toMissionId: missionId,
        fromSessionId: existing.id,
        closeReason: "rebind",
        detail: `Rebound ${tail} from ${existing.missionId} to ${missionId}.`,
      }),
    );
  }
  events.push(
    audit(at, tail, {
      kind: "open",
      sessionId: opened.id,
      missionId,
      fromSessionId: existing?.id,
      detail: existing ? `Opened after rebind on ${tail}.` : `Opened on ${tail}.`,
    }),
  );
  const next: FlightSessionBook = {
    sessions: [...sessions, opened],
    audit: [...normalized.audit, ...events],
  };
  return { ok: true, book: enforceOneOpenSessionPerTail(next, at), session: opened, rebound: Boolean(existing) };
}

export function closeFlightSession(
  book: FlightSessionBook,
  input: {
    sessionId?: string;
    missionId?: string;
    tail?: string;
    reason: FlightSessionCloseReason;
    at?: Date | string;
  },
): FlightSessionBook {
  const now = input.at instanceof Date ? input.at : input.at ? new Date(input.at) : new Date();
  const at = Number.isNaN(now.getTime()) ? new Date() : now;
  const iso = at.toISOString();
  const tail = normalizeSessionTail(input.tail);
  const hit = book.sessions.find((row) => {
    if (!isOpenFlightSession(row)) return false;
    if (input.sessionId && row.id === input.sessionId) return true;
    if (input.missionId && row.missionId === input.missionId) return true;
    if (tail && row.tail === tail) return true;
    return false;
  });
  if (!hit) return book;
  return {
    sessions: book.sessions.map((row) => (row.id === hit.id ? closeRow(row, input.reason, iso) : row)),
    audit: [
      ...book.audit,
      audit(at, hit.tail, {
        kind: input.reason === "orphan_boot" ? "orphan_boot" : "close",
        sessionId: hit.id,
        missionId: hit.missionId,
        closeReason: input.reason,
        detail: `Closed ${hit.tail} (${input.reason}).`,
      }),
    ],
  };
}

/**
 * Boot closes open sessions older than 6 hours when telemetry is missing or stale.
 * A fresh telemetry time keeps the open session.
 */
export function closeOrphanSessionsOnBoot(
  book: FlightSessionBook,
  input?: { now?: Date; maxAgeMs?: number; lastTelemetryAt?: string | null },
): { book: FlightSessionBook; closedIds: string[] } {
  const now = input?.now ?? new Date();
  const maxAge = input?.maxAgeMs ?? ORPHAN_BOOT_MAX_AGE_MS;
  const telemetry = isoOf(input?.lastTelemetryAt);
  if (telemetry) {
    const age = now.getTime() - new Date(telemetry).getTime();
    if (Number.isFinite(age) && age >= 0 && age < maxAge) {
      return { book, closedIds: [] };
    }
  }
  const closedIds: string[] = [];
  let next = book;
  for (const row of book.sessions) {
    if (!isOpenFlightSession(row)) continue;
    const opened = new Date(row.openedAt).getTime();
    if (!Number.isFinite(opened)) continue;
    if (now.getTime() - opened <= maxAge) continue;
    closedIds.push(row.id);
    next = closeFlightSession(next, { sessionId: row.id, reason: "orphan_boot", at: now });
  }
  return { book: next, closedIds };
}

/**
 * The jury never opens a session. A matching callsign or track is a suggestion only.
 */
export function jurySessionDecision(): { open: false; rebind: false; suggestOnly: true } {
  return { open: false, rebind: false, suggestOnly: true };
}

export function refuseJurySessionOpen(book: FlightSessionBook): FlightSessionBook {
  const decision = jurySessionDecision();
  if (decision.open || decision.rebind) return book;
  return book;
}

/**
 * Older careers stored the bind only on the company. The first open session
 * is created from that bind. An existing open session for the tail is left alone.
 */
export function sessionsFromLegacyBinding(
  book: FlightSessionBook,
  input: { missionId?: string | null; tail?: string | null; at?: string | null; source?: FlightSessionSource },
): FlightSessionBook {
  const missionId = input.missionId?.trim() ?? "";
  const tail = normalizeSessionTail(input.tail);
  if (!missionId || !tail) return book;
  if (openSessionForTail(book.sessions, tail)) return book;
  if (book.sessions.some((row) => row.missionId === missionId && isOpenFlightSession(row))) return book;
  const opened = openFlightSession(book, {
    missionId,
    tail,
    at: input.at || undefined,
    source: input.source ?? "manual",
    rebind: false,
  });
  return opened.ok ? opened.book : book;
}

export function tailForMission(mission: Pick<Mission, "registration" | "aircraftId"> | null | undefined): string {
  return normalizeSessionTail(mission?.registration || mission?.aircraftId);
}

export function bindingTail(
  missions: readonly Mission[],
  company: Partial<CompanyProfile> | null | undefined,
): string {
  const id = company?.activeFlightId?.trim() ?? "";
  if (!id) return "";
  return tailForMission(missions.find((row) => row.id === id));
}
